/* global Android */
import React, { useEffect, useRef, useState } from "react";
import PrimaryNavbar from "./PrimaryNavbar";
import * as RB from "react-bootstrap";
import Logo from "../Components/Logo";
import { isMobile } from "react-device-detect";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import { useLocation } from "react-router-dom";

function Sidebar() {
  const [menuClass, setmenuClass] = useState(
    "main-sidebar sidebar-style-2 offcanvas-lg offcanvas-start"
  );

  const osRef = useRef(null); // OverlayScrollbars ref
  const location = useLocation();

  // ===== Helpers (no class changes) =====
  const getViewportEl = () => {
    const inst = osRef.current?.osInstance?.();
    return inst?.elements?.().viewport || null;
  };

  const exactOrPrefixMatch = (container, currentPath) => {
    // exact first
    let el =
      container.querySelector(`a.nav-link[href='${currentPath}']`) ||
      container.querySelector(`a[href='${currentPath}']`);
    if (el) return el;

    // prefix (dynamic URLs)
    const links = container.querySelectorAll("a.nav-link, a");
    for (const a of links) {
      const href = a.getAttribute("href");
      if (!href) continue;
      if (href.startsWith("/") && currentPath.startsWith(href)) return a;
    }
    return null;
  };

  const ensureSubmenuOpen = (activeEl) => {
    const dropdownMenu = activeEl.closest("ul.dropdown-menu");
    if (dropdownMenu && !dropdownMenu.classList.contains("show")) {
      dropdownMenu.classList.add("show");
      const parentLi = dropdownMenu.closest("li.main-sidebar-li");
      if (parentLi && !parentLi.classList.contains("active")) {
        parentLi.classList.add("active");
      }
    }
  };

  const centerInViewport = (el) => {
    const viewport = getViewportEl();
    if (!viewport || !el) return;

    // Compute the ideal top so the element is vertically centered.
    // Using offsetTop relative to the scrollable content is fine here since
    // OverlayScrollbars keeps the DOM structure; viewport scrolls natively.
    const desired =
      el.offsetTop - (viewport.clientHeight - el.clientHeight) / 2;

    const top = Math.max(desired, 0);
    if (viewport.scrollTo) {
      viewport.scrollTo({ top, behavior: "smooth" });
    } else {
      viewport.scrollTop = top;
    }
  };

  const scrollActiveIntoView = () => {
    const container = document.getElementById("sidebar-ul-menu");
    if (!container) return false;

    const currentPath = `${location.pathname}`;
    const activeEl =
      container.querySelector("a.nav-link.active") ||
      exactOrPrefixMatch(container, currentPath);

    if (!activeEl) return false;

    ensureSubmenuOpen(activeEl);
    centerInViewport(activeEl);
    return true;
  };

  // ===== Your existing setup (unchanged) =====
  useEffect(() => {
    if (isMobile) {
      setmenuClass("main-sidebar sidebar-style-2 offcanvas offcanvas-start");
    }

    // PWA Pull-to-Refresh Fix
    const sidebar = document.getElementById("sidebar-wrapper");
    const preventPullToRefresh = (e) => {
      const body = document.body;
      const isSidebarOpen =
        body.classList.contains("sidebar-show") ||
        body.classList.contains("offcanvas-open");

      if (!isSidebarOpen || !sidebar) return;

      const isInsideSidebar = sidebar.contains(e.target);

      if (!isInsideSidebar) {
        e.preventDefault();
      } else {
        const scrollTop = sidebar.scrollTop;
        const scrollHeight = sidebar.scrollHeight;
        const offsetHeight = sidebar.offsetHeight;
        const atTop = scrollTop <= 0;
        const atBottom = scrollTop + offsetHeight >= scrollHeight;
        if (atTop || atBottom) e.preventDefault();
      }
    };
    document.addEventListener("touchmove", preventPullToRefresh, {
      passive: false,
    });

    // Android Bridge Integration
    const menuScroll = document.querySelector(".menu-scroll");
    const offcanvas = document.getElementById("main-sidebar");
    const callAndroidMenu = (status) => {
      if (window.Android && typeof Android.MenuOpen === "function") {
        try {
          Android.MenuOpen(status);
        } catch (err) {
          console.warn("Android.MenuOpen call failed:", err);
        }
      }
    };
    const handleTouchStart = () => callAndroidMenu(true);
    const handleTouchEnd = () => callAndroidMenu(false);

    menuScroll?.addEventListener("touchstart", handleTouchStart);
    menuScroll?.addEventListener("touchend", handleTouchEnd);

    offcanvas?.addEventListener("shown.bs.offcanvas", () => {
      callAndroidMenu(true);
      // try a few times after offcanvas opens (DOM ready + measured)
      let tries = 0;
      const max = 10;
      const tick = () => {
        const ok = scrollActiveIntoView();
        if (!ok && tries++ < max) setTimeout(tick, 100);
      };
      setTimeout(tick, 100);
    });
    offcanvas?.addEventListener("hidden.bs.offcanvas", () =>
      callAndroidMenu(false)
    );

    return () => {
      document.removeEventListener("touchmove", preventPullToRefresh);
      menuScroll?.removeEventListener("touchstart", handleTouchStart);
      menuScroll?.removeEventListener("touchend", handleTouchEnd);
      offcanvas?.removeEventListener("shown.bs.offcanvas", () =>
        callAndroidMenu(true)
      );
      offcanvas?.removeEventListener("hidden.bs.offcanvas", () =>
        callAndroidMenu(false)
      );
    };
  }, []);

  // ===== Route change: robust retries (handles remounts/async menus) =====
  useEffect(() => {
    let tries = 0;
    const max = 15; // ~1.5s total
    const delay = 100;

    const attempt = () => {
      const ok = scrollActiveIntoView();
      if (!ok && tries++ < max) setTimeout(attempt, delay);
    };

    const start = setTimeout(attempt, 150); // let OverlayScrollbars mount
    return () => clearTimeout(start);
  }, [location.pathname]);

  // ===== Render (classes/markup unchanged) =====
  return (
    <>
      <nav className="navbar navbar-expand-lg sidebar-navbar"></nav>
      <div id="main-sidebar" className={menuClass}>
        <button
          type="button"
          className="btn-close mb-3 pb-3 mx-auto d-inline-block d-lg-none"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={() => {
            if (window.Android?.MenuOpen) Android.MenuOpen(false);
          }}
        ></button>

        {/* OverlayScrollbars wrapper (replaces PerfectScrollbar 1:1) */}
        <OverlayScrollbarsComponent
          options={{ scrollbars: { autoHide: "leave" } }}
          ref={osRef}
          style={{ height: "100vh", overflowY: "auto" }}
        >
          <aside id="sidebar-wrapper" className="menu-scroll">
            <div className="sidebar-brand">
              <Logo />
            </div>
            <div className="sidebar-brand sidebar-brand-sm">
              <a href="index.html">VURA</a>
            </div>
            <PrimaryNavbar />
          </aside>
        </OverlayScrollbarsComponent>
      </div>
    </>
  );
}

export default Sidebar;
